import React from "react";

import { Button, useTheme } from "@mui/material";

import { useLegacyAuth } from "src/domains/auth/module/components/AuthProvider";

import { OAuthError } from "../types/OAuthError";
interface GithubButtonProps {
  onSuccess?: (response: Record<string, unknown>) => void;
  onError?: (error: OAuthError) => void;
  disabled?: boolean;
  fullWidth?: boolean;
}

const GithubButton: React.FC<GithubButtonProps> = ({
  onSuccess,
  onError,
  disabled = false,
  fullWidth = true,
}) => {
  const { authInProgress, setAuthInProgress } = useLegacyAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const handleGithubLogin = async () => {
    try {
      setAuthInProgress(true);

      // Initialize GitHub OAuth - use relative path that will be proxied to API
      const githubAuthUrl = "/api/v1/auth/github";

      // Open GitHub OAuth popup
      const popup = window.open(
        githubAuthUrl,
        "github-oauth",
        "width=500,height=600,scrollbars=yes,resizable=yes",
      );

      if (!popup) {
        throw new Error("Popup blocked. Please allow popups for this site.");
      }

      // Listen for message from popup
      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;

        if (event.data.type === "GITHUB_OAUTH_SUCCESS") {
          popup.close();
          window.removeEventListener("message", handleMessage);
          setAuthInProgress(false);
          onSuccess?.(event.data.user);
        } else if (event.data.type === "GITHUB_OAUTH_ERROR") {
          popup.close();
          window.removeEventListener("message", handleMessage);
          setAuthInProgress(false);
          onError?.(event.data.error);
        }
      };

      window.addEventListener("message", handleMessage);

      // Handle popup closed manually
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          window.removeEventListener("message", handleMessage);
          setAuthInProgress(false);
        }
      }, 1000);
    } catch (error) {
      setAuthInProgress(false);
      onError?.(error as OAuthError);
    }
  };

  return (
    <Button
      variant="outlined"
      fullWidth={fullWidth}
      disabled={disabled || authInProgress}
      onClick={handleGithubLogin}
      startIcon={
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 1.27a11 11 0 00-3.48 21.46c.55.1.75-.24.75-.53v-1.86c-3.03.66-3.67-1.46-3.67-1.46-.5-1.26-1.21-1.6-1.21-1.6-.99-.68.07-.66.07-.66 1.09.08 1.66 1.12 1.66 1.12.97 1.66 2.55 1.18 3.17.9.1-.7.38-1.18.69-1.45-2.43-.28-4.98-1.21-4.98-5.39 0-1.19.43-2.16 1.12-2.92-.11-.28-.49-1.4.11-2.92 0 0 .92-.3 3 1.11a10.43 10.43 0 015.52 0c2.08-1.41 3-1.11 3-1.11.6 1.52.22 2.64.11 2.92.7.76 1.12 1.73 1.12 2.92 0 4.19-2.55 5.11-4.99 5.38.39.34.74 1 .74 2.02v3c0 .29.2.64.76.53A11 11 0 0012 1.27z" />
        </svg>
      }
      sx={{
        py: "10px",
        px: "14px",
        height: "40px",
        borderRadius: "10px",
        textTransform: "none",
        fontWeight: 500,
        fontSize: "0.8125rem",
        borderWidth: 1,
        bgcolor: isDark ? theme.palette.grey[800] : theme.palette.grey[50],
        borderColor: isDark ? theme.palette.grey[600] : theme.palette.grey[200],
        color: "text.primary",
        mb: "10px",
        gap: "10px",
        transition: "all 150ms ease",
        "& .MuiButton-startIcon": { marginRight: 0, marginLeft: 0 },
        "&:hover": {
          bgcolor: isDark ? theme.palette.grey[700] : theme.palette.grey[100],
          borderColor: isDark
            ? theme.palette.grey[500]
            : theme.palette.grey[300],
        },
        "&:active": { transform: "scale(0.98)" },
        "&:disabled": {
          bgcolor: isDark ? theme.palette.grey[800] : theme.palette.grey[50],
          borderColor: isDark
            ? theme.palette.grey[600]
            : theme.palette.grey[200],
          color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
        },
      }}
    >
      {authInProgress ? "Connecting..." : "Continue with GitHub"}
    </Button>
  );
};

export default GithubButton;
